// Slider ([data-slider] with a scroll-snap [data-slider-track] of [data-slide]s and an empty [data-slider-dots]
// row): one dot per slide, built here so slides added later (Google reviews) get theirs on a 'slider:refresh'
// event. The dots scroll to their slide, follow swipes and only show while the row overflows. Optional
// [data-slider-prev] / [data-slider-next] buttons (Bildergalerie) move by one slide and are disabled at the ends.
// No autoplay (it would move content away from people who read slowly).
export function initSliders() {
	document.querySelectorAll('[data-slider]').forEach(setupSlider)
}

function setupSlider(slider) {
	const track = slider.querySelector('[data-slider-track]')
	const dotsRow = slider.querySelector('[data-slider-dots]')
	const label = slider.dataset.sliderLabel || ''
	if (!track) return

	let slides = []
	let dots = []
	let current = 0
	let target = null // a dot click scrolls here; the slides passed on the way do not count
	let visible = new Set()
	let observer

	const mark = (index) => {
		current = index
		dots.forEach((dot, i) => dot.toggleAttribute('aria-current', i === index))
	}
	// offsets between slides, so it does not matter which element is the offsetParent
	const go = (index, behavior = 'smooth') => {
		if (!slides[index]) return
		target = behavior === 'smooth' && !visible.has(index) ? index : null
		track.scrollTo({ left: slides[index].offsetLeft - slides[0].offsetLeft, behavior })
		mark(index)
	}
	const toggleDots = () => {
		if (dotsRow) dotsRow.hidden = slides.length < 2 || track.scrollWidth <= track.clientWidth + 1
	}
	const prev = slider.querySelector('[data-slider-prev]')
	const next = slider.querySelector('[data-slider-next]')
	const updateArrows = () => {
		if (prev) prev.disabled = track.scrollLeft <= 1
		if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1
	}
	prev?.addEventListener('click', () => go(Math.max(0, current - 1)))
	next?.addEventListener('click', () => go(Math.min(slides.length - 1, current + 1)))
	if (prev || next) track.addEventListener('scroll', updateArrows, { passive: true })

	const build = () => {
		slides = [...track.querySelectorAll('[data-slide]')]
		if (dotsRow) {
			dots = slides.map((slide, i) => {
				const dot = document.createElement('button')
				dot.type = 'button'
				dot.className = 'slider-dot'
				if (slide.id) dot.setAttribute('aria-controls', slide.id)
				dot.setAttribute('aria-label', `${label} ${i + 1}${slide.dataset.slideTitle ? ': ' + slide.dataset.slideTitle : ''}`.trim())
				dot.addEventListener('click', () => go(i))
				return dot
			})
			dotsRow.replaceChildren(...dots)
		}

		// keep the dots in step with swipes and keyboard scrolling: the current dot stays while its slide is
		// visible (several narrow slides, e.g. logos, can be visible at once), otherwise the first visible one
		observer?.disconnect()
		visible = new Set()
		observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const i = slides.indexOf(entry.target)
					entry.isIntersecting ? visible.add(i) : visible.delete(i)
				})
				if (target !== null) {
					if (visible.has(target)) target = null
					return
				}
				if (visible.size && !visible.has(current)) mark(Math.min(...visible))
			},
			{ root: track, threshold: 0.6 },
		)
		slides.forEach((slide) => observer.observe(slide))
		mark(Math.min(current, slides.length - 1))
		toggleDots()
		updateArrows()
	}

	slider.addEventListener('slider:refresh', build)
	// lazy images (logos without fixed sizes) widen the row only once loaded; until then every slide looks
	// visible, so observe again with the real sizes
	track.addEventListener('load', () => {
		toggleDots()
		observer?.disconnect()
		visible.clear()
		slides.forEach((slide) => observer.observe(slide))
	}, true)
	// a new width (rotation, resized window) keeps the current slide in place
	window.addEventListener('resize', () => {
		toggleDots()
		go(current, 'instant')
		updateArrows()
	})
	build()
}
